import React from 'react';
import pt from 'prop-types';

import View from 'components/view';
import Button from 'components/button';
import ButtonLink from 'components/button/link';
import ExternalLink from 'components/link/external';
import AppIcon from 'components/apps/icon';

import StorefrontPreviewImage from './preview-image';
import StorefrontUseCases from './use-cases';
import StorefrontThemeStyles from './theme-styles';
import StorefrontNewAppHighlights from './new-app-highlights';
import StorefrontNewAppFeatures from './new-app-features';
import StorefrontNewAppDeveloper from './new-app-developer';
import StorefrontNewAppRelatedThemes from './new-app-related-themes';

import { formatDate, inflect, locationWithQuery } from 'utils';
import { getAppSlugId, renderAppPrice, getAppDeveloperUrl } from 'utils/app';
import { renderMarkdown } from 'utils/string';

import './storefront.scss';

export default class StorefrontNewAppView extends React.PureComponent {
  static contextTypes = {
    client: pt.object.isRequired,
  };

  static propTypes = {
    id: pt.string.isRequired,
    apps: pt.object.isRequired,
    router: pt.object,
    location: pt.object,
    installedApps: pt.object.isRequired,
    createStorefront: pt.func.isRequired,
    onViewApp: pt.func.isRequired,
  };

  state = {
    styleId: null,
    styleAssets: null,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.id !== this.props.id) {
      this.setState({ styleId: null, styleAssets: null });
    }
  }

  onClickInstall = (event) => {
    event.preventDefault();
    const { id, apps, createStorefront } = this.props;

    const app = apps.results.find((app) => id === app.id);

    createStorefront(app, {
      $style: this.state.styleId,
    });
  };

  onChangeThemeStyle = (styleId, styleAssets) => {
    this.setState({ styleId, styleAssets });
  };

  getRelatedThemes = (app) => {
    const { apps } = this.props;
    const appSlugId = getAppSlugId(app);

    return apps.results.filter(
      (relatedApp) =>
        relatedApp.id !== app.id &&
        relatedApp.type === 'theme' &&
        relatedApp.published &&
        relatedApp.theme?.storefront?.app &&
        (relatedApp.theme.storefront.app === appSlugId ||
          relatedApp.theme.storefront.app === app.theme?.storefront?.app),
    );
  };

  render() {
    const { id, apps, installedApps, router, location, onViewApp } = this.props;
    const { client } = this.context;

    const backUrl = locationWithQuery(location, { app: undefined }, true);

    const app = apps.results.find((app) => id === app.id);

    if (!app) {
      router.replace(backUrl);
      return null;
    }

    const { type, name, description, demo_url, theme } = app;
    const { styleAssets } = this.state;
    const typeLabel = type === 'theme' ? 'Theme' : 'App';

    const installedApp = installedApps.results.find(
      (installedApp) => installedApp.app_id === app.id,
    );

    const themeStorefrontApp =
      theme?.storefront?.app &&
      apps.results.find((app) => getAppSlugId(app) === theme.storefront.app);

    const owner = app.partner || app.client;
    const developerUrl = getAppDeveloperUrl(app, owner);

    const requiresTheme = app.storefront?.theme?.provider === 'app';

    const relatedThemes = this.getRelatedThemes(app);

    return (
      <div className="storefront-apps-view" key={app.id}>
        <View uri={backUrl} backTitle="Back" detail={true}>
          <div className="storefront-apps-view-banner">
            <div className="storefront-apps-view-header">
              <div className="storefront-apps-view-title">
                <h1>{name}</h1>
                <span className="price">{renderAppPrice(app, client)}</span>
              </div>
              <div className="storefront-apps-view-subtitle">{description}</div>
              <div className="storefront-apps-view-actions">
                {requiresTheme ? (
                  <a
                    className={`button button-secondary button-md`}
                    href="#themes"
                  >
                    {inflect(relatedThemes.length, 'themes')} available
                  </a>
                ) : (
                  <Button
                    type="default"
                    size="md"
                    onClick={this.onClickInstall}
                  >
                    {installedApp
                      ? 'Create storefront'
                      : `Install ${typeLabel.toLowerCase()}`}
                  </Button>
                )}
                {demo_url && (
                  <ButtonLink type="secondary" size="md" href={demo_url}>
                    View demo store
                  </ButtonLink>
                )}
              </div>
              {type === 'theme' ? (
                <>
                  {themeStorefrontApp && (
                    <div className="storefront-apps-view-header-section">
                      <label>Storefront app</label>
                      <div
                        className="storefront-apps-info-theme-app"
                        onClick={onViewApp.bind(this, themeStorefrontApp)}
                      >
                        <AppIcon app={themeStorefrontApp} size={24} />
                        {themeStorefrontApp.name}
                      </div>
                    </div>
                  )}
                  <div className="storefront-apps-view-header-section">
                    <StorefrontUseCases app={app} />
                  </div>
                  {theme?.styles?.length > 0 && (
                    <div className="storefront-apps-view-header-section">
                      Color schemes
                      <StorefrontThemeStyles
                        theme={theme}
                        onChangeStyle={this.onChangeThemeStyle}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="storefront-apps-view-header-section">
                    <StorefrontUseCases app={app} />
                  </div>
                </>
              )}
              <div className="storefront-apps-view-header-details">
                <div className="storefront-apps-view-header-detail">
                  <label>Developer</label>
                  <div>
                    {developerUrl ? (
                      <ExternalLink href={developerUrl} icon={false}>
                        {owner.name}
                      </ExternalLink>
                    ) : (
                      owner.name
                    )}
                  </div>
                </div>
                <div className="storefront-apps-view-header-detail">
                  <label>Updated</label>
                  <div>
                    {formatDate(
                      app.date_version_updated || app.date_created,
                      'short',
                    )}
                  </div>
                </div>
                <div className="storefront-apps-view-header-detail">
                  <label>Version</label>
                  <div>{app.version}</div>
                </div>
              </div>
            </div>
            <div className="storefront-apps-view-preview-image">
              <StorefrontPreviewImage
                app={app}
                width={1096}
                height={740}
                styleAssets={styleAssets}
                mobile={true}
              />
            </div>
          </div>
          {app.full_description?.length > 0 && (
            <div className="storefront-apps-view-full-description">
              {renderMarkdown(app.full_description)}
            </div>
          )}
        </View>
        <div className="storefront-apps-view-body">
          <div className="storefront-apps-view-body-container">
            <StorefrontNewAppHighlights app={app} />
            <StorefrontNewAppFeatures app={app} />
            <StorefrontNewAppDeveloper app={app} />
          </div>
        </div>
        <a name="themes" />
        {relatedThemes.length > 0 && (
          <div className="storefront-apps-view-footer">
            <div className="storefront-apps-view-body-container">
              <StorefrontNewAppRelatedThemes
                {...this.props}
                app={app}
                themes={relatedThemes}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}
